import React, { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Box, Divider } from '@mui/material';
import {
  Air,
  Sync,
  AutoGraph,
  DoDisturb
} from '@mui/icons-material';
import { displayCFDState, selectedCFDState } from 'features/cfd/state/cfdAtoms';
import { apiRenderKeyState } from 'shared/state/renderKeyAtoms';
import { authTokenState, cognitoIdState } from 'features/auth/state/authAtoms';
import { yamlConfigState } from 'features/optimization/state/yamlConfigAtoms';
import { CustomButton } from 'shared/ui/CustomButton';
import { submitCFDStart } from 'features/cfd/api/submitCFDStart';
import { fetchCFDStop } from 'features/cfd/api/fetchCFDStop';
import { CustomTypography } from 'shared/ui/CustomTypography';
import { canStartCfd } from 'features/cfd/utils/yamlValidation';

const subtitleSx = {
  display: 'flex',
  alignItems: 'center',
};

export const CFDButtons: React.FC = () => {
  const setDisplayCFD = useSetRecoilState(displayCFDState);
  const setRenderKey = useSetRecoilState(apiRenderKeyState);

  const authToken = useRecoilValue(authTokenState);
  const cognitoId = useRecoilValue(cognitoIdState);
  const selectedCFD = useRecoilValue(selectedCFDState);
  const yamlConfig = useRecoilValue(yamlConfigState);

  const [startLoading, setStartLoading] = useState<boolean>(false);
  const [resultLoading, setResultLoading] = useState<boolean>(false);
  const [cancelLoading, setCancelLoading] = useState<boolean>(false);

  const startClick = async () => {
    // YAMLの最低条件を満たした場合のみ実行する
    if (canStartCfd(yamlConfig)) {
      setStartLoading(true);
      await submitCFDStart(
        yamlConfig,
        authToken,
        cognitoId,
      );
      setRenderKey((p) => p + 1);
      setStartLoading(false);
    }
  };

  const resultClick = async () => {
    if (selectedCFD) {
      setResultLoading(true);
      if (selectedCFD.status_code === 200) {
        setDisplayCFD(selectedCFD);
      } else if (selectedCFD.status_code === 201) {
        alert("The specified CFD is still running.")
        setDisplayCFD(null);
      } else {
        alert("The specified CFD failed. Please rerun or contact your account manager.")
        setDisplayCFD(null);
      }
      setResultLoading(false);
    }
  };

  const cancelClick = async () => {
    if (selectedCFD) {
      setCancelLoading(true);
      await fetchCFDStop(authToken, selectedCFD.name);
      setRenderKey((p: number) => p + 1);
      setCancelLoading(false);
    }
  };

  return (
    <Box component="div" sx={{ boxSizing: 'border-box', flexGrow: 1, paddingTop: 1 }}>

      <Box component="div" sx={subtitleSx}>
        <CustomTypography>CFD APIs</CustomTypography>
      </Box>

      <Divider sx={{ width: '100%', my: 0.5, bgcolor: '#e1e1e1' }} />

      <Box component="div" sx={{ display: 'flex', gap: 0.5 }}>
        <Box component="div" sx={{ flex: 2 }}>
          <CustomButton
            size='small'
            disabled={startLoading}
            onClick={startClick}
            loading={startLoading}
            loadingPosition='start'
            startIcon={<Air />}
            variant='contained'
            sx={{ fontSize: '10px', width: '100%' }}
          >
            Run CFD
          </CustomButton>
        </Box >

        <Box component="div" sx={{ flex: 2 }}>
          <CustomButton
            size='small'
            onClick={() => { setRenderKey((p) => p + 1) }}
            loadingPosition='start'
            startIcon={<Sync />}
            variant='contained'
            sx={{ fontSize: '10px', width: '100%' }}
          >
            Sync
          </CustomButton>
        </Box >

      </Box >

      <Box component="div" sx={{ display: 'flex', gap: 0.5, paddingTop: 0.5 }}>
        <Box component="div" sx={{ flex: 2 }}>
          <CustomButton
            size='small'
            disabled={true}
            onClick={cancelClick}
            loading={cancelLoading}
            loadingPosition='start'
            startIcon={<DoDisturb />}
            variant='contained'
            sx={{ fontSize: '10px', width: '100%' }}
          >
            Cancel
          </CustomButton>
        </Box >

        <Box component="div" sx={{ flex: 2 }}>
          <CustomButton
            size='small'
            disabled={!selectedCFD}
            onClick={resultClick}
            loading={resultLoading}
            loadingPosition='start'
            startIcon={<AutoGraph />}
            variant='contained'
            sx={{ fontSize: '10px', width: '100%' }}
          >
            Show Result
          </CustomButton>
        </Box >

      </Box >

    </Box >
  );
};
